/**
 * Storage handlers (storage:*).
 *
 * `storage:read` mirrors the studio-spec fixtures by parsing JSON when
 * possible and falling back to the raw string when parsing fails.
 */

import type { FlowStep } from "../types/flow";
import { parsePath } from "../expressions/path";
import { resolveValue } from "../expressions/resolve";
import { registerStepHandler } from "../runtime/step-registry";

function asRecord(step: FlowStep): Record<string, unknown> {
 return step as unknown as Record<string, unknown>;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
 return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

async function readStoredValue(
 storage: {
 read(key: string): Promise<string | null> | string | null;
 },
 key: string,
): Promise<unknown> {
 const raw = await storage.read(key);

 if (raw === null) return undefined;
 if (typeof raw !== "string") return raw;

 const trimmed = raw.trim();
 if (!trimmed) return raw;

 try {
 return JSON.parse(raw);
 } catch {
 return raw;
 }
}

registerStepHandler("storage:read", async (step, ctx) => {
 if (!ctx.storage) return;

 const r = asRecord(step);
 const keyValue = resolveValue(r["key"], ctx);
 const key = typeof keyValue === "string" ? keyValue.trim() : "";
 if (!key) return;

 const target = typeof r["output"] === "string"
 ? r["output"]
 : typeof r["out"] === "string"
 ? r["out"]
 : "";

 if (!target) return;

 const parsed = parsePath(target);
 if (!parsed) return;

 const parsedValue = await readStoredValue(ctx.storage, key);
 if (parsedValue === undefined) return;

 ctx.writeState(parsed.scope, parsed.segments, parsedValue);
});

registerStepHandler("storage:write", async (step, ctx) => {
 if (!ctx.storage) return;

 const r = asRecord(step);
 const keyValue = resolveValue(r["key"], ctx);
 const key = typeof keyValue === "string" ? keyValue.trim() : "";
 if (!key) return;

 const value = resolveValue(r["v"] ?? r["value"], ctx);
 const serialized = typeof value === "string" ? value : JSON.stringify(value);
 if (serialized === undefined) return;

 await ctx.storage.write(key, serialized);
});

registerStepHandler("storage:patch", async (step, ctx) => {
 if (!ctx.storage) return;

 const r = asRecord(step);
 const keyValue = resolveValue(r["key"], ctx);
 const key = typeof keyValue === "string" ? keyValue.trim() : "";
 if (!key) return;

 const patchSource = resolveValue(r["patch"] ?? r["v"], ctx);
 if (!isPlainObject(patchSource)) return;

 const current = await readStoredValue(ctx.storage, key);
 const currentObject = isPlainObject(current) ? current : {};
 const next = {
 ...currentObject,
 ...patchSource,
 };

 await ctx.storage.write(key, JSON.stringify(next));
});

registerStepHandler("storage:del", async (step, ctx) => {
 if (!ctx.storage) return;

 const r = asRecord(step);
 const keyValue = resolveValue(r["key"], ctx);
 const key = typeof keyValue === "string" ? keyValue.trim() : "";
 if (!key) return;

 await ctx.storage.delete(key);
});